import type { BatMetrics, LengthUnit, NormalizedProfile, RawProfilePoint } from '@/types/profile';

/**
 * Average density used for weight estimation, in g/cm^3. Wood bats vary by
 * species and moisture content (maple ~0.63-0.75, birch ~0.55-0.65,
 * ash ~0.55-0.65). We use a representative hard-maple figure since that is
 * Mark Lumber's primary stock; this is clearly an ESTIMATE and labeled as
 * such in the UI, not a substitute for a scale reading.
 */
const ASSUMED_DENSITY_G_PER_CM3 = 0.68;

/** Sweet spot is conventionally ~150mm (~6in) from the barrel tip on a wood bat. */
const SWEET_SPOT_OFFSET_FROM_TIP_MM = 150;

/** Volume + centroid of a single conical frustum segment between two profile points. */
function segmentVolumeAndMoment(p0: RawProfilePoint, p1: RawProfilePoint) {
  const h = p1.position - p0.position;
  if (h <= 0) return { volume: 0, moment: 0 };

  const a = p0.radius;
  const b = p1.radius - p0.radius;

  // Volume of a conical frustum: pi*h*(r0^2 + r0*r1 + r1^2) / 3
  const volume = (Math.PI * h * (a * a + a * p1.radius + p1.radius * p1.radius)) / 3;

  // First moment about the segment's own start (t=0), derived from
  // integrating pi*t*r(t)^2 dt over t in [0,h] with r(t) = a + b*(t/h).
  const momentLocal = Math.PI * h * h * (a * a / 2 + (2 * a * b) / 3 + (b * b) / 4);
  // Convert local moment (about p0.position) to a global moment about position 0.
  const moment = momentLocal + volume * p0.position;

  return { volume, moment };
}

function computeVolumeAndBalance(points: RawProfilePoint[]) {
  let totalVolume = 0;
  let totalMoment = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const { volume, moment } = segmentVolumeAndMoment(points[i], points[i + 1]);
    totalVolume += volume;
    totalMoment += moment;
  }
  const balancePoint = totalVolume > 0 ? totalMoment / totalVolume : 0;
  return { totalVolumeMm3: totalVolume, balancePointMm: balancePoint };
}

/**
 * Finds the handle diameter: the minimum radius in the gripping section,
 * defined as the run of points before the barrel's peak radius, excluding
 * the immediate knob taper (first 5% of the profile) so the knob's own
 * flare doesn't get mistaken for "the handle."
 */
function findHandleRadius(points: RawProfilePoint[], barrelIndex: number): number {
  const startIdx = Math.max(1, Math.floor(points.length * 0.05));
  const endIdx = Math.max(startIdx + 1, barrelIndex);
  let min = Infinity;
  for (let i = startIdx; i < endIdx; i++) {
    if (points[i].radius < min) min = points[i].radius;
  }
  return Number.isFinite(min) ? min : points[0].radius;
}

export function computeMetrics(
  profile: NormalizedProfile,
  modelName: string,
  displayUnit: LengthUnit,
): BatMetrics {
  const points = profile.points;
  if (points.length < 2) {
    throw new Error('Cannot compute metrics from an empty profile.');
  }

  const first = points[0];
  const last = points[points.length - 1];
  const lengthMm = last.position - first.position;

  let barrelIndex = 0;
  for (let i = 0; i < points.length; i++) {
    if (points[i].radius > points[barrelIndex].radius) barrelIndex = i;
  }
  const barrelRadiusMm = points[barrelIndex].radius;
  const knobRadiusMm = first.radius;
  const handleRadiusMm = findHandleRadius(points, barrelIndex);

  const { totalVolumeMm3, balancePointMm } = computeVolumeAndBalance(points);

  const sweetSpotMm = Math.max(0, Math.min(lengthMm, lengthMm - SWEET_SPOT_OFFSET_FROM_TIP_MM));

  const volumeCm3 = totalVolumeMm3 / 1000;
  const estimatedWeightGrams = volumeCm3 * ASSUMED_DENSITY_G_PER_CM3;

  let minRadius = Infinity;
  let maxRadius = -Infinity;
  for (const p of points) {
    if (p.radius < minRadius) minRadius = p.radius;
    if (p.radius > maxRadius) maxRadius = p.radius;
  }

  const metrics: BatMetrics = {
    modelName,
    units: displayUnit,
    length: lengthMm,
    barrelDiameter: barrelRadiusMm * 2,
    handleDiameter: handleRadiusMm * 2,
    knobDiameter: knobRadiusMm * 2,
    sweetSpotPosition: sweetSpotMm,
    balancePoint: balancePointMm,
    totalProfilePoints: points.length,
    boundingBox: { x: lengthMm, y: maxRadius * 2, z: maxRadius * 2 },
    volume: totalVolumeMm3,
    estimatedWeightGrams,
  };

  return metrics;
}
